import * as React from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { QRCodeSVG } from 'qrcode.react'
import AuthProvider from '../../providers/auth/cognitoProvider'
import { useNotify } from 'react-admin'

export default function MfaSetupDialog() {
  const [open, setOpen] = React.useState(false)
  const [step, setStep] = React.useState('INIT') // INIT, SETUP, VERIFY
  const [secretCode, setSecretCode] = React.useState('')
  const [verificationCode, setVerificationCode] = React.useState('')
  const [username, setUsername] = React.useState('')
  const notify = useNotify()

  React.useEffect(() => {
    checkMfaStatus()
  }, [])

  const checkMfaStatus = () => {
    AuthProvider.getMfaStatus()
      .then((isEnabled) => {
        if (!isEnabled) {
          setOpen(true)
          // Get username for QR code label
          AuthProvider.getIdentity().then((identity) => {
            setUsername(identity.fullName || 'User')
          })
        }
      })
      .catch((err) => {
        // Silent fail if not logged in or other error
        console.error('Failed to check MFA status', err)
      })
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleStartSetup = () => {
    AuthProvider.setupMfa()
      .then((secret) => {
        // AWS provider may return either the raw shared secret or an otpauth:// URI
        let rawSecret = secret
        try {
          if (typeof secret === 'string' && secret.startsWith('otpauth://')) {
            // attempt to parse the secret query param
            const url = new URL(secret)
            const s = url.searchParams.get('secret')
            if (s) rawSecret = s
          }
        } catch (e) {
          // ignore parse errors and fall back to using the returned string as the secret
        }

        setSecretCode(rawSecret)
        setStep('SETUP')
      })
      .catch((error) => {
        notify('Failed to initiate MFA setup: ' + error.message, { type: 'error' })
      })
  }

  const handleVerify = () => {
    AuthProvider.verifyMfa({ code: verificationCode })
      .then(() => {
        return AuthProvider.enableMfa()
      })
      .then(() => {
        notify('MFA Enabled Successfully', { type: 'success' })
        setOpen(false)
      })
      .catch((error) => {
        // Human readable error for common cases
        const msg = error.message || ''
        if (msg.includes('CodeMismatch')) {
          notify('Invalid code. Please try again.', { type: 'error' })
        } else {
          notify('Verification failed: ' + msg, { type: 'error' })
        }
      })
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enable Two-Factor Authentication</DialogTitle>
      <DialogContent>
        {step === 'INIT' && (
          <DialogContentText>
            To keep your account secure, we recommend enabling Two-Factor Authentication (MFA).
            Would you like to set it up now?
          </DialogContentText>
        )}

        {step === 'SETUP' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 2 }}>
            <Typography variant="body1">
              Scan this QR code with your authenticator app (e.g. Google Authenticator).
            </Typography>
            {secretCode && (
              <Box sx={{ p: 2, bgcolor: 'white' }}>
                <QRCodeSVG
                  value={`otpauth://totp/${encodeURIComponent('MaleFromHome:' + username)}?secret=${encodeURIComponent(
                    secretCode
                  )}&issuer=${encodeURIComponent('MaleFromHome')}`}
                  size={200}
                />
              </Box>
            )}
            <Typography variant="caption" color="text.secondary">
              Secret: {secretCode}
            </Typography>

            <TextField
              autoFocus
              margin="dense"
              label="Verification Code"
              type="text"
              fullWidth
              variant="outlined"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              inputProps={{ maxLength: 6 }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'INIT' ? (
          <>
            <Button onClick={handleClose}>Not Now</Button>
            <Button onClick={handleStartSetup} variant="contained" autoFocus>
              Setup Now
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleVerify} variant="contained" disabled={verificationCode.length !== 6}>
              Verify & Enable
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
